import { useTranslation } from '../../../i18n';
import React, { useState, useEffect } from 'react';
import {
    Card,
    Table,
    Button,
    Space,
    Modal,
    Form,
    Input,
    message,
    Popconfirm,
    Tag,
    Select,
    Alert,
    Spin
} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    FileTextOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import hostSharedService from '../../../../shared/services/hostSharedService';

const { TextArea } = Input;
const { Option } = Select;

const normalizePaths = (payload) => {
    if (!payload) {
        return [];
    }

    if (Array.isArray(payload)) {
        return payload.map((item, index) => ({
            id: item.id ?? index,
            path: item.path || item.value || item || '/'
        }));
    }

    if (typeof payload === 'object') {
        return Object.keys(payload).map((key) => ({
            id: Number(key),
            path: payload[key]
        }));
    }

    return [];
};

const RewriteManager = ({ vhostName }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [paths, setPaths] = useState([]);
    const [templateList, setTemplateList] = useState([]);
    const [templateLoading, setTemplateLoading] = useState(false);
    const [templateApplying, setTemplateApplying] = useState(false);
    const [editorVisible, setEditorVisible] = useState(false);
    const [editorFetching, setEditorFetching] = useState(false);
    const [savingRewrite, setSavingRewrite] = useState(false);
    const [currentPath, setCurrentPath] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        if (!vhostName) return;
        loadPaths();
        loadTemplates();
    }, [vhostName]);

    const loadPaths = async () => {
        setLoading(true);
        try {
            const response = await hostSharedService.getRewritePaths(vhostName);
            if (response.success) {
                setPaths(normalizePaths(response.data));
            } else {
                setPaths([]);
                message.error(response.error || t('Failed to retrieve rewrite rule directory'));
            }
        } catch (error) {
            setPaths([]);
            message.error(t('Failed to retrieve rewrite rule directory: {{message}}', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };

    const loadTemplates = async () => {
        setTemplateLoading(true);
        try {
            const response = await hostSharedService.getRewriteRules(vhostName);
            if (response.success) {
                const list = Array.isArray(response.data)
                    ? response.data.map((item) => (typeof item === 'string' ? item : item?.name)).filter(Boolean)
                    : [];
                setTemplateList(list);
            }
        } catch (error) {
            console.error('加载伪静态模板失败:', error);
        } finally {
            setTemplateLoading(false);
        }
    };

    const handleOpenEditor = async (record) => {
        setCurrentPath(record);
        setEditorVisible(true);
        form.resetFields();
        setEditorFetching(true);
        try {
            const response = await hostSharedService.getRewriteContent(vhostName, record.id);
            if (response.success) {
                form.setFieldsValue({
                    content: response.data?.content || ''
                });
            } else {
                message.error(response.error || t('Failed to retrieve rewrite rules'));
                setEditorVisible(false);
            }
        } catch (error) {
            message.error(t('Failed to retrieve rewrite rules: {{message}}', { message: error.message }));
            setEditorVisible(false);
        } finally {
            setEditorFetching(false);
        }
    };

    const handleSaveRewrite = async () => {
        if (!currentPath) return;

        try {
            const values = await form.validateFields();
            setSavingRewrite(true);
            const response = await hostSharedService.setRewriteContent(vhostName, currentPath.id, values.content || '');
            if (response.success) {
                message.success(t('URL rewrite rules saved'));
                setEditorVisible(false);
            } else {
                message.error(response.error || t('Failed to save'));
            }
        } catch (error) {
            if (!error.errorFields) {
                message.error(t('Failed to save: {{message}}', { message: error.message }));
            }
        } finally {
            setSavingRewrite(false);
        }
    };

    const handleApplyTemplate = async (templateName) => {
        if (!templateName) return;
        setTemplateApplying(true);
        try {
            const response = await hostSharedService.getRewriteRuleContent(vhostName, templateName);
            if (response.success) {
                form.setFieldsValue({
                    content: response.data?.content || ''
                });
                message.success(t('Template rules applied'));
            } else {
                message.error(response.error || t('Failed to load template'));
            }
        } catch (error) {
            message.error(t('Failed to load template: {{message}}', { message: error.message }));
        } finally {
            setTemplateApplying(false);
        }
    };

    const handleClearRewrite = async (record) => {
        setLoading(true);
        try {
            const response = await hostSharedService.deleteRewrite(vhostName, record.id);
            if (response.success) {
                message.success(t('URL rewrite rules cleared for this directory'));
            } else {
                message.error(response.error || t('Failed to empty'));
            }
        } catch (error) {
            message.error(t('Failed to empty: {{message}}', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: t('Directory'),
            dataIndex: 'path',
            key: 'path',
            render: (text) => (
                <Space>
                    <FileTextOutlined />
                    <Tag color="blue">{text || '/'}</Tag>
                </Space>
            )
        },
        {
            title: t('Actions'),
            key: 'action',
            width: 260,
            render: (_, record) => (
                <Space>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleOpenEditor(record)}>
                        {t('Edit rule')}
                    </Button>
                    <Popconfirm
                        title={t('Clear all Rewrite rules for this directory?')}
                        onConfirm={() => handleClearRewrite(record)}
                        okText={t('OK')}
                        cancelText={t('Cancel')}
                    >
                        <Button type="link" danger size="small" icon={<DeleteOutlined />}>
                            {t('Empty')}
                        </Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Card
            title={
                <Space>
                    <FileTextOutlined />
                    {t('URL rewrite management')}
                </Space>
            }
            extra={
                <Button icon={<ReloadOutlined />} onClick={loadPaths}>
                    {t('Refresh')}
                </Button>
            }
        >
            <Alert
                message={t('Description')}
                description={t('Each bound directory has its own .htaccess file, where you can edit the full Rewrite rules. For quick setup, use an official template (WordPress, ThinkPHP, etc.). Changes take effect as soon as you save.')}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />

            <Table
                columns={columns}
                dataSource={paths}
                rowKey="id"
                loading={loading}
                pagination={false}
            />

            <Modal
                title={t('Edit {{value}} rewrite rules', { value: currentPath?.path || '' })}
                open={editorVisible}
                onOk={handleSaveRewrite}
                onCancel={() => setEditorVisible(false)}
                confirmLoading={savingRewrite}
                width={720}
                destroyOnClose
            >
                <Spin spinning={editorFetching}>
                    <Form form={form} layout="vertical">
                        {templateList.length > 0 && (
                            <Form.Item label={t('Quick apply template')}>
                                <Select
                                    placeholder={t('Select a template')}
                                    loading={templateLoading}
                                    onSelect={handleApplyTemplate}
                                    allowClear
                                    disabled={templateApplying}
                                >
                                    {templateList.map((rule) => (
                                        <Option key={rule} value={rule}>
                                            {rule}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}

                        <Form.Item label={t('.htaccess rules')} name="content">
                            <TextArea
                                rows={16}
                                style={{ fontFamily: 'monospace' }}
                                placeholder={t('# Paste rewrite rules here. Leave blank to remove URL rewriting for the current directory.')}
                            />
                        </Form.Item>
                    </Form>
                </Spin>
            </Modal>
        </Card>
    );
};

export default RewriteManager;
